import React, { useCallback, useState } from 'react'
import Link from 'next/link'
import cn from 'classnames'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import { usePrice } from '@corratech/pylot-price'
import { useDataLayerAction } from '@corratech/pylot-tag-manager'
import { formatCurrency } from '@lib/utils/formatCurrency'
import { useElementOnScreen } from '@lib/hooks/useElementOnScreen'
import s from './ProductTileMobile.module.scss'
import { ProductTileProps, Variant } from '../ProductTile/ProductTile'

const categoryUrlSuffix = '.html'

export const ProductTileMobile = ({
    product = {},
    imageParam = {}
}: ProductTileProps): JSX.Element | null => {
    const {
        url_key,
        media_gallery,
        badge_image_text: badgeImageText,
        badge_caption_icon: badgeCaptionIcon,
        customTextAfterProductImage,
        flag
    } = product

    const swatchAttr = 'color'
    const colorsImagesMap = new Map()
    const productUrl = `/producto/${url_key}${categoryUrlSuffix}`
    const isConfig = product.__typename === 'ConfigurableProduct'
    const fullImageUrl = useCallback(
        (imageUrl?: string): string => {
            const widthString = imageParam.width
                ? `&width=${imageParam.width}`
                : ''
            return `${imageUrl}?auto=webp&format=pjpg${widthString}`
        },
        [imageParam.width]
    )

    if (isConfig && product.variants) {
        product.variants.map((variant: Variant) => {
            const colorVariant = variant.attributes.find(
                (variant: { code: string }) => variant?.code === swatchAttr
            )

            const img = variant.product.small_image
                ? variant.product.small_image.url
                : false

            if (colorVariant && img) {
                colorsImagesMap.set(colorVariant.label, fullImageUrl(img))
            }
        })
    }

    const dataLayerAction = useDataLayerAction()
    const { total: productTotalPrice } = usePrice(product.price_range)
    const [elementRef, isVisible] = useElementOnScreen()
    const [image] = useState(fullImageUrl(product.small_image?.url))
    const getSecondaryImage: () => string | undefined = useCallback(() => {
        const [, secondaryImage] = media_gallery || []
        if (secondaryImage) {
            return fullImageUrl(secondaryImage.url) as string
        }

        return
    }, [media_gallery])

    const pushProductClickEvent = () => {
        const totalCurrency = formatCurrency(productTotalPrice)

        dataLayerAction({
            type: 'PRODUCT_CLICK',
            data: {
                ...product,
                price: parseFloat(
                    totalCurrency
                        .replace('$', '')
                        .replace('.', '')
                        .replace(',', '.')
                ),
                currencyCode:
                    product.price_range?.minimum_price?.final_price?.currency
            }
        })
    }

    const badgeText = flag ? flag.label : badgeImageText

    if (!product) return null

    return (
        <Link href={productUrl}>
            <div aria-hidden onClick={pushProductClickEvent}>
                <a className={cn(s['product-link'])} aria-label={product.name}>
                    <div
                        className={cn(s['product-wrapper'], {
                            show: isVisible
                        })}
                        ref={elementRef}
                    >
                        <div className={s['product-badge']}>{badgeText}</div>
                        <div className={s['product-img']}>
                            <img
                                src={image}
                                alt={
                                    product.name ? product.name : 'placeholder'
                                }
                                className={cn(s['product-img'], 'contain')}
                            />
                            <img
                                src={getSecondaryImage() || image}
                                alt={
                                    product.name ? product.name : 'placeholder'
                                }
                                className={cn(
                                    s['productimg-secondary'],
                                    'contain'
                                )}
                            />
                        </div>
                        <div className={s['product-details']}>
                            {customTextAfterProductImage && (
                                <div className={s['product-custom-text']}>
                                    {customTextAfterProductImage}
                                </div>
                            )}
                            {badgeCaptionIcon && (
                                <div
                                    className={s['product-badge-caption']}
                                    dangerouslySetInnerHTML={{
                                        __html: badgeCaptionIcon
                                    }}
                                />
                            )}
                            <h6 className={cn(s['product-name'])}>
                                <span
                                    className={s['product-name-link']}
                                    title={product.name}
                                >
                                    {product.name}
                                </span>
                            </h6>
                            <ProductPrice
                                className={String(
                                    product.__typename
                                ).toLowerCase()}
                                priceRange={product.price_range}
                            />
                            {/* TODO: Add dropdown to change variant if required  */}
                        </div>
                    </div>
                </a>
            </div>
        </Link>
    )
}

export const ProductItemMobile = React.memo(
    (props: ProductTileProps) => <ProductTileMobile {...props} />,
    (prevProps, newProps) =>
        JSON.stringify(prevProps) === JSON.stringify(newProps)
)

ProductItemMobile.displayName = 'MemoProductItemMobile'
